import { flow, set, isNull } from 'lodash/fp';
import { Reducer } from 'redux';

import { LoginActionTypes } from '../../../auth/login/store/types';
import { SubCounterpartyState, SubCounterpartyType, SubCounterpartyActionTypes } from './types';

export const initialSubCounterparty: SubCounterpartyType = {
    subCounterpartyName: '',
    jurisdiction: '',
    subCounterpartyDescription: null,
    parentCounterpartyType: '',
    scopes: [],
    isSystem: 0
};

export const INITIAL_STATE: SubCounterpartyState = {
    availableOpinionSubCounterpartyTypes: [],
    jurisdictionalOpinionSubCounterpartyTypes: [],
    selectedSubCounterparty: null,
    savedSelectedSubCounterparty: null,
    isEditing: false,
    jurisdiction: null,
    parentCounterparty: null,
    isFetching: false,
    multiParentCounterparties: [],
    savedParentCounterparties: [],
    subCounterpartyTypesToDelete: null,
    isDeleting: false,
    scopeFilters: []
};

export const subCounterpartyReducer: Reducer<SubCounterpartyState> = (state = INITIAL_STATE, action): SubCounterpartyState => {
    switch (action.type) {
        case SubCounterpartyActionTypes.FETCH_OPINION_SUB_COUNTERPARTY_TYPES_STARTED:
            return set('isFetching', true, state);
        case SubCounterpartyActionTypes.FETCH_OPINION_SUB_COUNTERPARTY_TYPES_SUCCESSFUL:
            return flow(
                set('availableOpinionSubCounterpartyTypes', action.payload),
                set('isFetching', false)
            )(state);
        case SubCounterpartyActionTypes.FETCH_OPINION_SUB_COUNTERPARTY_TYPES_FAILED:
            return flow(
                set('fetchError', action.payload),
                set('isFetching', false)
            )(state);
        case SubCounterpartyActionTypes.RESET_AVAILABLE_SUB_COUNTERPARTY_TYPES:
            return set('availableOpinionSubCounterpartyTypes', [], state);
        case SubCounterpartyActionTypes.SET_JURISDICTION_SUB_COUNTERPARTY_TYPES:
            return set('jurisdictionalOpinionSubCounterpartyTypes', action.payload, state);
        case SubCounterpartyActionTypes.SET_SELECTED_OPINION_SUB_COUNTERPARTY:
            return flow(
                set('selectedSubCounterparty', action.payload),
                set('savedSelectedSubCounterparty', action.payload),
                set('isEditing', true)
            )(state);
        case SubCounterpartyActionTypes.UPDATE_OPINION_SUB_COUNTERPARTY_VALUE: {
            const { key, value } = action.payload;
            if (isNull(state.selectedSubCounterparty)) {
                const selected = { ...initialSubCounterparty, [key]: value };
                return set('selectedSubCounterparty', selected, state);
            }
            return set(`selectedSubCounterparty[${key}]`, value, state);
        }
        case SubCounterpartyActionTypes.UPSERT_OPINION_SUB_COUNTERPARTY_TYPES_STARTED:
            return set('isSaving', true, state);
        case SubCounterpartyActionTypes.UPSERT_OPINION_SUB_COUNTERPARTY_TYPES_SUCCESSFUL: {
            const updatedJurisdictionalOpinionSubCounterpartyTypes = !state.selectedSubCounterparty?.subCounterpartyTypeId ? [...state.jurisdictionalOpinionSubCounterpartyTypes, state.selectedSubCounterparty] : state.jurisdictionalOpinionSubCounterpartyTypes;
            return flow(
                set('jurisdictionalOpinionSubCounterpartyTypes', updatedJurisdictionalOpinionSubCounterpartyTypes),
                set('selectedSubCounterparty', null),
                set('savedSelectedSubCounterparty', null),
                set('multiParentCounterparties', []),
                set('savedParentCounterparties', []),
                set('isSaving', false),
                set('isEditing', false)
            )(state);
        }
        case SubCounterpartyActionTypes.UPSERT_OPINION_SUB_COUNTERPARTY_TYPES_FAILED:
            return flow(
                set('saveError', action.payload),
                set('isSaving', false)
            )(state);
        case SubCounterpartyActionTypes.SET_SUB_COUNTERPARTY_JURISDICTION:
            return set('jurisdiction', action.payload, state);
        case SubCounterpartyActionTypes.SET_SUB_COUNTERPARTY_PARENT:
            return set('parentCounterparty', action.payload, state);
        case SubCounterpartyActionTypes.ADD_OPINION_SUB_COUNTERPARTY_TYPE: {
            if (!isNull(state.parentCounterparty)) {
                return flow(
                    set('selectedSubCounterparty', { ...initialSubCounterparty, isSystem: action.payload }),
                    set('isEditing', true),
                    set('multiParentCounterparties', [state.parentCounterparty])
                )(state);
            }
            return flow(
                set('selectedSubCounterparty', { ...initialSubCounterparty, isSystem: action.payload }),
                set('isEditing', true)
            )(state);
        }
        case SubCounterpartyActionTypes.REVERT_SELECTED_SUB_COUNTERPARTY_CHANGES:
            return flow(
                set('selectedSubCounterparty', null),
                set('savedSelectedSubCounterparty', null),
                set('isEditing', false),
                set('multiParentCounterparties', []),
                set('savedParentCounterparties', [])
            )(state);
        case SubCounterpartyActionTypes.UPDATE_MULTI_PARENT_COUNTERPARTIES:
            return set('multiParentCounterparties', action.payload, state);
        case SubCounterpartyActionTypes.SET_SAVED_MULTI_PARENT_COUNTERPARTIES:
            return flow(
                set('multiParentCounterparties', action.payload),
                set('savedParentCounterparties', action.payload)
            )(state);
        case SubCounterpartyActionTypes.UPDATE_DELETE_OPINION_SUB_COUNTERPARTY_TYPES:
            return set('subCounterpartyTypesToDelete', action.payload, state);
        case SubCounterpartyActionTypes.DELETE_OPINION_SUB_COUNTERPARTY_TYPES_STARTED:
            return set('isDeleting', true, state);
        case SubCounterpartyActionTypes.DELETE_OPINION_SUB_COUNTERPARTY_TYPES_SUCCESSFUL:
            return flow(
                set('isDeleting', false),
                set('subCounterpartyTypesToDelete', null)
            )(state);
        case SubCounterpartyActionTypes.DELETE_OPINION_SUB_COUNTERPARTY_TYPES_FAILED:
            return set('isDeleting', false, state);
        case SubCounterpartyActionTypes.SET_OPINION_SUB_COUNTERPARTY_SCOPE_FILTERS:
            return set('scopeFilters', action.payload, state);
        case LoginActionTypes.LOGOUT_SUCCESSFUL:
            return INITIAL_STATE;
        default:
            return state;
    }
};
